import React, { useState, useRef, useEffect, useMemo } from 'react';
import { SlidersHorizontal, ChevronDown, Check, X, Clock } from 'lucide-react';

export const PRESET_OPTIONS = [
  { id: 'all', label: 'All Time' },
  { id: 'last_7_days', label: 'Last 7 Days', days: 7 },
  { id: 'last_2_weeks', label: 'Last 2 Weeks', days: 14 },
  { id: 'last_3_weeks', label: 'Last 3 Weeks', days: 21 },
  { id: 'last_4_weeks', label: 'Last 4 Weeks', days: 28 },
  { id: 'last_1_month', label: 'Last 1 Month', days: 30 },
];

export const calculateDateBounds = (presetId) => {
  if (!presetId || presetId === 'all') {
    return { startDate: null, endDate: null, label: 'All Time' };
  }

  const now = new Date();
  const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

  if (presetId === 'last_1_month') {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate(), 0, 0, 0, 0);
    return {
      startDate: start.toISOString(),
      endDate: endOfDay.toISOString(),
      label: 'Last 1 Month',
    };
  }

  const preset = PRESET_OPTIONS.find((p) => p.id === presetId);
  const days = preset?.days || 7;
  const start = new Date(endOfDay.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
  start.setHours(0, 0, 0, 0);

  return {
    startDate: start.toISOString(),
    endDate: endOfDay.toISOString(),
    label: preset?.label || `Last ${days} Days`,
  };
};

export const formatDDMMYYYY = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

export const formatShortDisplay = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const currentYear = new Date().getFullYear();
  if (d.getFullYear() !== currentYear) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Parses user-typed date in multiple formats:
 * - DD-MM-YYYY, DD/MM/YYYY, DD.MM.YYYY
 * - YYYY-MM-DD, YYYY/MM/DD
 * - DDMMYYYY (8 digits)
 */
export const parseManualDate = (str) => {
  if (!str || typeof str !== 'string') return null;
  const trimmed = str.trim();
  if (!trimmed) return null;

  // 1. DD-MM-YYYY or DD/MM/YYYY or DD.MM.YYYY
  const dmyMatch = trimmed.match(/^(\d{1,2})[-/.\s](\d{1,2})[-/.\s](\d{4})$/);
  if (dmyMatch) {
    const day = parseInt(dmyMatch[1], 10);
    const month = parseInt(dmyMatch[2], 10);
    const year = parseInt(dmyMatch[3], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1970 && year <= 2100) {
      const d = new Date(year, month - 1, day, 0, 0, 0, 0);
      if (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) {
        return d;
      }
    }
    return null;
  }

  // 2. YYYY-MM-DD or YYYY/MM/DD
  const ymdMatch = trimmed.match(/^(\d{4})[-/.\s](\d{1,2})[-/.\s](\d{1,2})$/);
  if (ymdMatch) {
    const year = parseInt(ymdMatch[1], 10);
    const month = parseInt(ymdMatch[2], 10);
    const day = parseInt(ymdMatch[3], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1970 && year <= 2100) {
      const d = new Date(year, month - 1, day, 0, 0, 0, 0);
      if (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) {
        return d;
      }
    }
    return null;
  }

  // 3. DDMMYYYY (8 digits)
  const numMatch = trimmed.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (numMatch) {
    const day = parseInt(numMatch[1], 10);
    const month = parseInt(numMatch[2], 10);
    const year = parseInt(numMatch[3], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1970 && year <= 2100) {
      const d = new Date(year, month - 1, day, 0, 0, 0, 0);
      if (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) {
        return d;
      }
    }
    return null;
  }

  return null;
};

/**
 * CommandCenterDateFilter
 * 
 * Trips page filter with:
 * - SlidersHorizontal filter icon matching design
 * - Manual date typing inputs (From & To in DD-MM-YYYY)
 * - Quick predefined periods (All Time, Last 7 Days, etc.)
 * - Live error validation and clear shortcuts
 */
const CommandCenterDateFilter = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const activePeriod = value?.period || 'all';

  const [startDateInput, setStartDateInput] = useState(() => (value?.startDate ? formatDDMMYYYY(value.startDate) : ''));
  const [endDateInput, setEndDateInput] = useState(() => (value?.endDate ? formatDDMMYYYY(value.endDate) : ''));
  const [errorMessage, setErrorMessage] = useState('');

  // Sync internal inputs when prop value changes
  useEffect(() => {
    if (value?.startDate) {
      setStartDateInput(formatDDMMYYYY(value.startDate));
    } else {
      setStartDateInput('');
    }

    if (value?.endDate) {
      setEndDateInput(formatDDMMYYYY(value.endDate));
    } else {
      setEndDateInput('');
    }
    setErrorMessage('');
  }, [value?.startDate, value?.endDate, value?.period]);

  // Click outside and Escape key handler
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Handle Preset Selection
  const handleSelectPreset = (presetId) => {
    setErrorMessage('');
    if (presetId === 'all') {
      setStartDateInput('');
      setEndDateInput('');
      onChange({ period: 'all', startDate: null, endDate: null, label: 'All Time' });
      setIsOpen(false);
      return;
    }

    const bounds = calculateDateBounds(presetId);
    setStartDateInput(formatDDMMYYYY(bounds.startDate));
    setEndDateInput(formatDDMMYYYY(bounds.endDate));
    onChange({ period: presetId, ...bounds });
    setIsOpen(false);
  };

  // Apply Manual Date Inputs
  const handleApplyManual = () => {
    const rawStart = startDateInput.trim();
    const rawEnd = endDateInput.trim();

    if (!rawStart && !rawEnd) {
      setErrorMessage('Please type a date (DD-MM-YYYY)');
      return;
    }

    let startParsed = null;
    let endParsed = null;

    if (rawStart) {
      startParsed = parseManualDate(rawStart);
      if (!startParsed) {
        setErrorMessage('Invalid From Date. Use DD-MM-YYYY');
        return;
      }
    }

    if (rawEnd) {
      endParsed = parseManualDate(rawEnd);
      if (!endParsed) {
        setErrorMessage('Invalid To Date. Use DD-MM-YYYY');
        return;
      }
    }

    // If only one date is typed, apply for that single whole day
    if (startParsed && !endParsed) {
      endParsed = new Date(startParsed);
    } else if (!startParsed && endParsed) {
      startParsed = new Date(endParsed);
    }

    // Verify start <= end
    if (startParsed.getTime() > endParsed.getTime()) {
      setErrorMessage('From Date cannot be after To Date');
      return;
    }

    const startIso = new Date(
      startParsed.getFullYear(),
      startParsed.getMonth(),
      startParsed.getDate(),
      0, 0, 0, 0
    ).toISOString();

    const endIso = new Date(
      endParsed.getFullYear(),
      endParsed.getMonth(),
      endParsed.getDate(),
      23, 59, 59, 999
    ).toISOString();

    const startShort = formatShortDisplay(startIso);
    const endShort = formatShortDisplay(endIso);
    const label = startShort === endShort ? startShort : `${startShort} – ${endShort}`;

    setErrorMessage('');
    onChange({
      period: 'custom',
      startDate: startIso,
      endDate: endIso,
      label,
    });
    setIsOpen(false);
  };

  // Quick Action: Today
  const handleSetToday = () => {
    const todayStr = formatDDMMYYYY(new Date());
    setStartDateInput(todayStr);
    setEndDateInput(todayStr);
    setErrorMessage('');
  };

  // Quick Action: Clear inputs
  const handleClearInputs = () => {
    setStartDateInput('');
    setEndDateInput('');
    setErrorMessage('');
  };

  // Clear all filters back to All Time
  const handleClearAll = (e) => {
    if (e) e.stopPropagation();
    setStartDateInput('');
    setEndDateInput('');
    setErrorMessage('');
    onChange({ period: 'all', startDate: null, endDate: null, label: 'All Time' });
    setIsOpen(false);
  };

  // Determine button display label
  const buttonLabel = useMemo(() => {
    if (activePeriod === 'all') return 'Filters';
    if (activePeriod === 'custom') {
      return value?.label || 'Custom Range';
    }
    const preset = PRESET_OPTIONS.find((p) => p.id === activePeriod);
    return preset?.label || 'Filtered';
  }, [activePeriod, value?.label]);

  const isFiltered = activePeriod !== 'all';

  return (
    <div className="relative inline-block text-left" ref={containerRef}>
      {/* Trigger Pill Button with SlidersHorizontal Icon */}
      <div className="inline-flex items-center">
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all cursor-pointer select-none ${
            isFiltered
              ? 'border-blue-400 bg-blue-50 text-blue-700 shadow-2xs font-semibold hover:bg-blue-100/70'
              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 shadow-2xs'
          }`}
          title={isFiltered ? `Active Filter: ${buttonLabel}` : 'Filter trips by date'}
        >
          <SlidersHorizontal className={`w-3.5 h-3.5 ${isFiltered ? 'text-blue-600' : 'text-gray-400'}`} />
          <span className="whitespace-nowrap">{buttonLabel}</span>
          {isFiltered ? (
            <span
              onClick={handleClearAll}
              className="ml-0.5 p-0.5 rounded-full hover:bg-blue-200 text-blue-700 cursor-pointer"
              title="Clear date filter"
              role="button"
            >
              <X className="w-3 h-3 stroke-[2.5]" />
            </span>
          ) : (
            <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          )}
        </button>
      </div>

      {/* Floating Popover Card */}
      {isOpen && (
        <div className="absolute right-0 sm:right-auto sm:left-0 mt-2 w-[310px] sm:w-[325px] rounded-2xl bg-white shadow-2xl border border-gray-100 p-4 z-50 animate-in fade-in zoom-in-95 duration-100">
          
          {/* Top Header */}
          <div className="flex items-center justify-between pb-2.5 border-b border-gray-100">
            <div className="flex items-center gap-1.5">
              <SlidersHorizontal className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-bold text-gray-800 uppercase tracking-wide">
                Filter by Date
              </span>
            </div>
            {isFiltered && (
              <button
                type="button"
                onClick={handleClearAll}
                className="text-[11px] font-semibold text-rose-600 hover:text-rose-700 hover:underline cursor-pointer"
              >
                Reset All
              </button>
            )}
          </div>

          {/* Quick Predefined Period Presets */}
          <div className="py-2.5">
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <Clock className="w-3 h-3 text-gray-400" />
              <span>Predefined Periods</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {PRESET_OPTIONS.map((preset) => {
                const isSelected = activePeriod === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => handleSelectPreset(preset.id)}
                    className={`flex items-center justify-between px-2.5 py-1 rounded-lg text-xs font-medium transition-all text-left cursor-pointer border ${
                      isSelected
                        ? 'bg-blue-600 text-white border-blue-600 shadow-2xs font-semibold'
                        : 'bg-gray-50/80 hover:bg-gray-100/80 text-gray-700 border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    <span>{preset.label}</span>
                    {isSelected && <Check className="w-3 h-3 stroke-[3] ml-1 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          <div className="relative my-1">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-100" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase font-semibold text-gray-400 bg-white px-2">
              <span>Or Type Date Range</span>
            </div>
          </div>

          {/* Manual Date Typing Inputs (No calendar picker) */}
          <div className="pt-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                  From Date
                </label>
                <input
                  type="text"
                  value={startDateInput}
                  onChange={(e) => {
                    setStartDateInput(e.target.value);
                    setErrorMessage('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleApplyManual();
                  }}
                  placeholder="DD-MM-YYYY"
                  className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-medium text-center"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                  To Date
                </label>
                <input
                  type="text"
                  value={endDateInput}
                  onChange={(e) => {
                    setEndDateInput(e.target.value);
                    setErrorMessage('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleApplyManual();
                  }}
                  placeholder="DD-MM-YYYY"
                  className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-medium text-center"
                />
              </div>
            </div>

            {/* Quick shortcuts & Format note */}
            <div className="flex items-center justify-between mt-1.5 px-0.5 text-[11px]">
              <span className="text-gray-400">e.g. 28-10-2026</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSetToday}
                  className="text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
                >
                  Today
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={handleClearInputs}
                  className="text-gray-500 hover:text-gray-700 font-medium cursor-pointer"
                >
                  Clear
                </button>
              </div>
            </div>

            {errorMessage && (
              <p className="text-[11px] text-rose-500 font-medium mt-1.5 text-center">
                {errorMessage}
              </p>
            )}

            {/* Apply Button */}
            <div className="pt-2.5">
              <button
                type="button"
                onClick={handleApplyManual}
                className="w-full py-2 px-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-lg text-xs font-semibold shadow-xs transition-all cursor-pointer text-center"
              >
                Apply Filter
              </button>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default CommandCenterDateFilter;
